/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * useVoiceChat — records microphone audio and sends the audio file to the LLM.
 *
 * State machine: idle -> recording -> processing -> speaking -> idle
 * The browser only records audio; speech understanding happens server-side by
 * passing the audio attachment to the selected model.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

export type VoiceState = 'idle' | 'recording' | 'processing' | 'speaking';

export interface UseVoiceChatOptions {
  /** Called with the completed recording. Caller should upload/send the file. */
  onVoiceRecording: (file: File) => void | Promise<void>;
  /** BCP-47 language tag for TTS playback (default "en-US"). */
  language?: string;
}

export interface UseVoiceChatReturn {
  voiceState: VoiceState;
  isVoiceOn: boolean;
  interimTranscript: string;
  toggleVoice: () => void;
  speakResponse: (text: string) => void;
  stopVoice: () => void;
  voices: SpeechSynthesisVoice[];
  selectedVoice: SpeechSynthesisVoice | null;
  setSelectedVoice: (voice: SpeechSynthesisVoice) => void;
  voiceRate: number;
  setVoiceRate: (rate: number) => void;
  isRecordingSupported: boolean;
  isTTSSupported: boolean;
}

const LS_VOICE_NAME = 'paxia_voice_name';
const LS_VOICE_RATE = 'paxia_voice_rate';
const LS_VOICE_LANG = 'paxia_voice_lang';

function loadString(key: string, fallback: string): string {
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
}

function loadNumber(key: string, fallback: number): number {
  try { const v = localStorage.getItem(key); return v ? Number(v) : fallback; } catch { return fallback; }
}

function persist(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* quota exceeded */ }
}

const isTTSSupportedGlobal =
  typeof window !== 'undefined' && 'speechSynthesis' in window;

const isRecordingSupportedGlobal =
  typeof window !== 'undefined'
  && typeof navigator !== 'undefined'
  && !!navigator.mediaDevices?.getUserMedia
  && typeof MediaRecorder !== 'undefined';

const AUDIO_MIME_CANDIDATES = [
  'audio/ogg;codecs=opus',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mpeg',
  'audio/wav',
];

function getSupportedAudioMimeType(): string {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return '';
  }
  return AUDIO_MIME_CANDIDATES.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || '';
}

function extensionForMimeType(mimeType: string): string {
  const normalized = mimeType.toLowerCase();
  if (normalized.includes('mp4')) return 'm4a';
  if (normalized.includes('mpeg')) return 'mp3';
  if (normalized.includes('wav')) return 'wav';
  if (normalized.includes('ogg')) return 'ogg';
  return 'webm';
}

export default function useVoiceChat(options: UseVoiceChatOptions): UseVoiceChatReturn {
  const { onVoiceRecording, language } = options;
  const lang = language ?? loadString(LS_VOICE_LANG, 'en-US');

  const onVoiceRecordingRef = useRef(onVoiceRecording);
  onVoiceRecordingRef.current = onVoiceRecording;

  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, _setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [voiceRate, _setVoiceRate] = useState(() => loadNumber(LS_VOICE_RATE, 1));

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const voiceStateRef = useRef<VoiceState>('idle');
  const isVoiceOnRef = useRef(false);
  const cancelRecordingRef = useRef(false);

  voiceStateRef.current = voiceState;

  const isVoiceOn = voiceState !== 'idle';
  const isRecordingSupported = isRecordingSupportedGlobal;

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (!isTTSSupportedGlobal) return;
    const load = () => {
      const availableVoices = speechSynthesis.getVoices();
      if (availableVoices.length) {
        setVoices(availableVoices);
        const savedName = loadString(LS_VOICE_NAME, '');
        const match = availableVoices.find((voice) => voice.name === savedName);
        _setSelectedVoice(match ?? availableVoices.find((voice) => voice.lang.startsWith('en')) ?? availableVoices[0]);
      }
    };
    load();
    speechSynthesis.addEventListener('voiceschanged', load);
    return () => speechSynthesis.removeEventListener('voiceschanged', load);
  }, []);

  const setSelectedVoice = useCallback((voice: SpeechSynthesisVoice) => {
    _setSelectedVoice(voice);
    persist(LS_VOICE_NAME, voice.name);
  }, []);

  const setVoiceRate = useCallback((rate: number) => {
    const clamped = Math.max(0.5, Math.min(2, rate));
    _setVoiceRate(clamped);
    persist(LS_VOICE_RATE, String(clamped));
  }, []);

  const finishRecording = useCallback(async (blob: Blob) => {
    if (cancelRecordingRef.current || blob.size === 0) {
      setVoiceState('idle');
      voiceStateRef.current = 'idle';
      isVoiceOnRef.current = false;
      setInterimTranscript('');
      return;
    }

    const mimeType = blob.type || getSupportedAudioMimeType() || 'audio/webm';
    const extension = extensionForMimeType(mimeType);
    const file = new File([blob], `voice-recording-${Date.now()}.${extension}`, { type: mimeType });

    setVoiceState('processing');
    voiceStateRef.current = 'processing';
    setInterimTranscript('');

    try {
      await onVoiceRecordingRef.current(file);
    } catch (error) {
      console.error('[useVoiceChat] voice recording send failed:', error);
      setVoiceState('idle');
      voiceStateRef.current = 'idle';
      isVoiceOnRef.current = false;
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (!isRecordingSupportedGlobal) return;

    try {
      cancelRecordingRef.current = false;
      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getSupportedAudioMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const recordedType = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: recordedType });
        chunksRef.current = [];
        mediaRecorderRef.current = null;
        releaseStream();
        void finishRecording(blob);
      };

      recorder.onerror = (event) => {
        console.error('[useVoiceChat] recorder error:', event);
        cancelRecordingRef.current = true;
        releaseStream();
        setVoiceState('idle');
        voiceStateRef.current = 'idle';
        isVoiceOnRef.current = false;
      };

      recorder.start();
      isVoiceOnRef.current = true;
      setVoiceState('recording');
      voiceStateRef.current = 'recording';
      setInterimTranscript('Recording voice message');
    } catch (error) {
      console.error('[useVoiceChat] failed to start recording:', error);
      releaseStream();
      setVoiceState('idle');
      voiceStateRef.current = 'idle';
      isVoiceOnRef.current = false;
      setInterimTranscript('');
    }
  }, [finishRecording, releaseStream]);

  const stopRecording = useCallback((submit: boolean) => {
    const recorder = mediaRecorderRef.current;
    cancelRecordingRef.current = !submit;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
      return;
    }
    releaseStream();
    if (!submit) {
      setVoiceState('idle');
      voiceStateRef.current = 'idle';
      isVoiceOnRef.current = false;
      setInterimTranscript('');
    }
  }, [releaseStream]);

  const speakResponse = useCallback((text: string) => {
    if (!isVoiceOnRef.current) return;

    if (!isTTSSupportedGlobal || !text.trim()) {
      setVoiceState('idle');
      voiceStateRef.current = 'idle';
      isVoiceOnRef.current = false;
      return;
    }

    speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    if (selectedVoice) utterance.voice = selectedVoice;
    utterance.rate = voiceRate;
    utterance.lang = lang;

    utterance.onstart = () => {
      setVoiceState('speaking');
      voiceStateRef.current = 'speaking';
    };

    utterance.onend = () => {
      setVoiceState('idle');
      voiceStateRef.current = 'idle';
      isVoiceOnRef.current = false;
    };

    utterance.onerror = (event) => {
      console.warn('[useVoiceChat] TTS error:', event.error);
      setVoiceState('idle');
      voiceStateRef.current = 'idle';
      isVoiceOnRef.current = false;
    };

    setVoiceState('speaking');
    voiceStateRef.current = 'speaking';
    speechSynthesis.speak(utterance);
  }, [selectedVoice, voiceRate, lang]);

  const stopVoice = useCallback(() => {
    isVoiceOnRef.current = false;
    stopRecording(false);
    if (isTTSSupportedGlobal) speechSynthesis.cancel();
    setVoiceState('idle');
    voiceStateRef.current = 'idle';
    setInterimTranscript('');
  }, [stopRecording]);

  const toggleVoice = useCallback(() => {
    if (voiceStateRef.current === 'recording') {
      stopRecording(true);
      return;
    }
    if (voiceStateRef.current !== 'idle') {
      stopVoice();
      return;
    }
    void startRecording();
  }, [startRecording, stopRecording, stopVoice]);

  useEffect(() => {
    return () => {
      isVoiceOnRef.current = false;
      cancelRecordingRef.current = true;
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch { /* ignore */ }
      }
      releaseStream();
      if (isTTSSupportedGlobal) speechSynthesis.cancel();
    };
  }, [releaseStream]);

  useEffect(() => {
    if (language) persist(LS_VOICE_LANG, language);
  }, [language]);

  return {
    voiceState,
    isVoiceOn,
    interimTranscript,
    toggleVoice,
    speakResponse,
    stopVoice,
    voices,
    selectedVoice,
    setSelectedVoice,
    voiceRate,
    setVoiceRate,
    isRecordingSupported,
    isTTSSupported: isTTSSupportedGlobal,
  };
}
